import { useAuth } from "../../../../../public/auth/infrastructure/store/auth-context";
import { useConstant } from "../../../../../shared/infrastructure/ui/hooks/use-constant";
import { useOffers } from "../../store";
import { useUpdateBlockedOffers } from "./use-update-offers";
import {
  BlockOfferEventAttr,
  OfferEventAttr,
} from "@private/offers/domain/entities/pusher-entity";
import Pusher, { Channel } from "pusher-js";
// @ts-expect-error - No declaration is required - TODO: - Stop using when the project is migrated.
import { REACT_APP_PUSHER_CLUSTER, REACT_APP_PUSHER_KEY } from "@env";
import { useCallback, useEffect } from "react";

if (process.env.NODE_ENV === "development") {
  Pusher.logToConsole = true;
}

export const usePusher = (): void => {
  const { user } = useAuth();
  const { newDataArrived } = useOffers();
  const updateBlockedOffers = useUpdateBlockedOffers();

  const pusher = useConstant(
    () =>
      new Pusher(REACT_APP_PUSHER_KEY as string, {
        cluster: REACT_APP_PUSHER_CLUSTER as string,
      })
  );

  const created = useCallback(
    (channel: Channel) => {
      channel.bind(
        "approval_created",
        ({ userOwner, data }: OfferEventAttr) => {
          if (user && user.user_id !== userOwner.user_id) {
            newDataArrived("new", data.approval_id.toString());
          }
        }
      );
    },
    [newDataArrived, user]
  );

  const updated = useCallback(
    (channel: Channel) => {
      channel.bind(
        "approval_updated",
        ({ userOwner, data }: OfferEventAttr) => {
          if (user && user.user_id !== userOwner.user_id) {
            newDataArrived("updated", data.approval_id.toString());
          }
        }
      );
    },
    [newDataArrived, user]
  );

  const removed = useCallback(
    (channel: Channel) => {
      channel.bind(
        "approval_removed",
        ({ userOwner, data }: OfferEventAttr) => {
          if (user && user.user_id !== userOwner.user_id) {
            newDataArrived("deleted", data.approval_id.toString());
          }
        }
      );
    },
    [newDataArrived, user]
  );

  const statusUpdated = useCallback(
    (channel: Channel) => {
      channel.bind(
        "approval_status_updated",
        ({ userOwner, data }: OfferEventAttr) => {
          if (user && user.user_id !== userOwner.user_id) {
            newDataArrived("status-changed", data.approval_id.toString());
          }
        }
      );
    },
    [newDataArrived, user]
  );

  const updateData = useCallback(
    ({ data }: BlockOfferEventAttr) => {
      updateBlockedOffers(data);
    },
    [updateBlockedOffers]
  );

  const blocked = useCallback(
    (channel: Channel) => {
      channel.bind("approval_being_blocked", updateData);
    },
    [updateData]
  );

  const unblocked = useCallback(
    (channel: Channel) => {
      channel.bind("approval_is_not_being_blocked", updateData);
    },
    [updateData]
  );

  useEffect(() => {
    const email = user?.email;

    if (email) {
      const pusherPattern = /([^A-Za-z0-9_\-=@,.;])/g;
      const channel = pusher.subscribe(email.replace(pusherPattern, "._."));

      channel.unbind_all();
      created(channel);
      updated(channel);
      removed(channel);
      statusUpdated(channel);
      blocked(channel);
      unblocked(channel);
    }

    return () => {
      if (email) {
        pusher.unsubscribe(email);
      }
    };
  }, [
    created,
    pusher,
    removed,
    statusUpdated,
    updated,
    blocked,
    unblocked,
    user?.email,
  ]);
};
